import React, { useEffect, useState } from "react";
import Select from "react-select";
import { getCurrencyList } from "../../../Services/CommonFunction";
import { Currency } from "../../../assets/interface/primeSharkWebsite_interface";
import { useDispatch, useSelector } from "react-redux";
import { storePrefferedCurrency } from "../../../config/action";

type CurrencyOption = {
  label: string;
  value: string;
  obj: Currency;
};
type CurrencySelectProps = {
  className?: string;
  value?: CurrencyOption | null;
  onChange: (selected: CurrencyOption | null) => void;
  isDisabled?: boolean;
  /** Force a fixed currency by code (e.g. "AED") and disable the control. */
  lockToCode?: string;
};

const CurrencySelect: React.FC<CurrencySelectProps> = ({
  value,
  onChange,
  className,
  isDisabled,
  lockToCode,
}) => {
  const [currencyOptions, setCurrencyOptions] = useState<CurrencyOption[]>([]);
  const selectedCurrency: Currency = useSelector(
    (state: any) => state?.items?.storedPrefferedCurrency,
  );

  let dispatch = useDispatch();

  useEffect(() => {
    if (lockToCode) {
      const fallbackObj = { code: lockToCode, currency: lockToCode, symbol: lockToCode === "USD" ? "$" : lockToCode } as any;
      onChange({ label: `${lockToCode} (${lockToCode})`, value: lockToCode, obj: fallbackObj });
    }

    getCurrencyList()
      .then((res: Currency[]) => {
        const options = res.map((item) => ({
          label: `${item.currency} (${item.code})`,
          value: item.code,
          obj: item,
        }));
        setCurrencyOptions(options);
        // Locked mode: force the fixed currency, sync it to redux (so the
        // session-charges symbol follows) and notify the parent form once.
        if (lockToCode) {
          const locked = options.find((o) => o.value === lockToCode);
          if (locked) {
            dispatch(storePrefferedCurrency(locked.obj));
            onChange(locked);
          }
        }
      })
      .catch(() => {})
      .finally(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockToCode]);
  return (
    <Select
      placeholder="Select preferred currency"
      className={className}
      menuPlacement="bottom"
      menuPosition="fixed" // Prevents screen shift
      menuPortalTarget={document.body}
      options={currencyOptions}
      isDisabled={isDisabled || !!lockToCode}
      value={
        lockToCode
          ? currencyOptions.find((o) => o.value === lockToCode)
            ? [currencyOptions.find((o) => o.value === lockToCode)!]
            : [{ label: `${lockToCode} (${lockToCode})`, value: lockToCode, obj: { code: lockToCode, currency: lockToCode, symbol: lockToCode === "USD" ? "$" : lockToCode } as any }]
          : selectedCurrency?.code
            ? [
                {
                  label: `${selectedCurrency.currency} (${selectedCurrency.code})`,
                  value: selectedCurrency?.code,
                  obj: selectedCurrency,
                },
              ]
            : []
      }
      onChange={(selectedOption) => {
        if (selectedOption?.value) {
          dispatch(storePrefferedCurrency(selectedOption.obj));
        }
        onChange(selectedOption);
      }}
      isClearable={false}
      styles={{
        menuPortal: (base) => ({
          ...base,
          zIndex: 999999,
        }),
        control: (baseStyles) => ({
          ...baseStyles,
          minHeight: "40px",
          height: "42px",
          // border: "none",
        }),
        valueContainer: (base) => ({
          ...base,
          height: "40px",
          padding: "0 8px",
        }),
        indicatorsContainer: (base) => ({
          ...base,
          height: "40px",
        }),
        placeholder: (base) => ({
          ...base,
          fontSize: "14px",
          fontWeight: 500,
          color: "#c2c2c2",
        }),
        singleValue: (base) => ({
          ...base,
          fontSize: "14px",
          fontWeight: 500,
        }),
        input: (base) => ({
          ...base,
          fontSize: "14px",
        }),
        menuList: (base) => ({
          ...base,
          maxHeight: "200px",
          padding: "0",
        }),
      }}
    />
  );
};

export default CurrencySelect;
